import type { AbilityCan } from '@/plugins/ability';

export type ProjectTab = 'overview' | 'offer' | 'attachments' | 'tracking' | 'comparison';
export type PermissionLevel = 'none' | 'read' | 'read_write';

export interface RoleConfig {
  readonly defineAbility: (can: AbilityCan, userId?: string) => void;
  readonly projects: {
    readonly canCreate: boolean;
    readonly canEdit: boolean;
    readonly canDelete: boolean;
    readonly canImport: boolean;
    readonly canGenerateReport: boolean;
    readonly canFinish: boolean;
    readonly canOverwriteProgressReport: boolean;
  };
  readonly contractors: {
    readonly canCreate: boolean;
    readonly canEdit: boolean;
    readonly canDelete: boolean;
  };
  readonly visibleProjectTabs: readonly ProjectTab[];
  readonly defaultClassifierPermission: PermissionLevel;
  readonly sidebar: {
    readonly canAccessContractors: boolean;
    readonly canAccessSupervisors: boolean;
  };
  readonly settings: {
    readonly canAccess: boolean;
    readonly canAccessSystemSettings: boolean;
    readonly canAccessUserManagement: boolean;
  };
  readonly isClassifierConfigurable: boolean;
  readonly labelKey: string;
}

function createReadonlyDepartmentConfig(labelKey: string): RoleConfig {
  return {
    defineAbility: (can: AbilityCan) => {
      can('read', 'Project');
      can('read', 'User');
    },
    projects: {
      canCreate: false,
      canEdit: false,
      canDelete: false,
      canImport: false,
      canGenerateReport: false,
      canFinish: false,
      canOverwriteProgressReport: false,
    },
    contractors: { canCreate: false, canEdit: false, canDelete: false },
    visibleProjectTabs: ['overview', 'attachments'],
    defaultClassifierPermission: 'none',
    sidebar: { canAccessContractors: true, canAccessSupervisors: false },
    settings: { canAccess: true, canAccessSystemSettings: false, canAccessUserManagement: false },
    isClassifierConfigurable: true,
    labelKey,
  };
}

const ALL_PROJECT_TABS: readonly ProjectTab[] = [
  'overview',
  'offer',
  'attachments',
  'tracking',
  'comparison',
];

export const ROLE_CONFIG = {
  admin: {
    defineAbility: (can: AbilityCan) => {
      can('manage', 'all');
    },
    projects: {
      canCreate: true,
      canEdit: true,
      canDelete: true,
      canImport: true,
      canGenerateReport: true,
      canFinish: true,
      canOverwriteProgressReport: true,
    },
    contractors: { canCreate: true, canEdit: true, canDelete: true },
    visibleProjectTabs: ALL_PROJECT_TABS,
    defaultClassifierPermission: 'read_write',
    sidebar: { canAccessContractors: true, canAccessSupervisors: true },
    settings: { canAccess: true, canAccessSystemSettings: true, canAccessUserManagement: true },
    isClassifierConfigurable: false,
    labelKey: 'auth.roles.admin',
  },

  supervisor: {
    defineAbility: (can: AbilityCan, userId?: string) => {
      can('read', 'Project', { supervisorId: userId } as never);
      can('finish', 'Project', { supervisorId: userId } as never);
      can('read', 'User', { id: userId } as never);
    },
    projects: {
      canCreate: false,
      canEdit: false,
      canDelete: false,
      canImport: false,
      canGenerateReport: true,
      canFinish: true,
      canOverwriteProgressReport: true,
    },
    contractors: { canCreate: false, canEdit: false, canDelete: false },
    visibleProjectTabs: ALL_PROJECT_TABS,
    defaultClassifierPermission: 'read_write',
    sidebar: { canAccessContractors: true, canAccessSupervisors: false },
    settings: { canAccess: true, canAccessSystemSettings: false, canAccessUserManagement: false },
    isClassifierConfigurable: true,
    labelKey: 'auth.roles.supervisor',
  },

  technical_department: {
    defineAbility: (can: AbilityCan) => {
      can('manage', 'Project');
      can('read', 'User');
      can('read', 'SupervisorStats');
    },
    projects: {
      canCreate: true,
      canEdit: true,
      canDelete: true,
      canImport: true,
      canGenerateReport: true,
      canFinish: true,
      canOverwriteProgressReport: true,
    },
    contractors: { canCreate: true, canEdit: true, canDelete: true },
    visibleProjectTabs: ALL_PROJECT_TABS,
    defaultClassifierPermission: 'read_write',
    sidebar: { canAccessContractors: true, canAccessSupervisors: true },
    settings: { canAccess: true, canAccessSystemSettings: true, canAccessUserManagement: false },
    isClassifierConfigurable: true,
    labelKey: 'auth.roles.technicalDepartment',
  },

  marketing: createReadonlyDepartmentConfig('auth.roles.marketing'),
  project_department: createReadonlyDepartmentConfig('auth.roles.projectDepartment'),
  contract_department: createReadonlyDepartmentConfig('auth.roles.contractDepartment'),
  accounting_department: createReadonlyDepartmentConfig('auth.roles.accountingDepartment'),
} as const satisfies Record<string, RoleConfig>;

/** UserRole derived from config keys — no separate type to maintain */
export type UserRole = keyof typeof ROLE_CONFIG;

/** Roles whose classifier permissions are configurable in admin settings */
export const CONFIGURABLE_ROLES = (Object.entries(ROLE_CONFIG) as [UserRole, RoleConfig][])
  .filter(([, config]) => config.isClassifierConfigurable)
  .map(([role]) => role);

/** Role options for user management dropdowns */
export const ROLE_OPTIONS = (Object.entries(ROLE_CONFIG) as [UserRole, RoleConfig][]).map(
  ([role, config]) => ({ value: role, labelKey: config.labelKey }),
);
